/**
 * API Types and Type Utilities
 *
 * This module provides type-safe utilities for working with the OpenAPI-generated types.
 * It enables automatic inference of request/response shapes based on API paths.
 */

import type { paths } from '@access-cms/api-types';
import { ResourceType } from '@access-cms/shared-constants';

// =============================================================================
// Path Type Utilities
// =============================================================================

/**
 * All available API paths from the OpenAPI spec
 */
export type ApiPaths = keyof paths;

/**
 * HTTP methods that exist on a given path
 */
export type PathMethods<P extends ApiPaths> = keyof paths[P] & ('get' | 'post' | 'put' | 'patch' | 'delete');

/**
 * Check if a path has a specific method
 */
export type HasMethod<P extends ApiPaths, M extends string> = M extends keyof paths[P]
  ? paths[P][M] extends { responses: object }
    ? true
    : false
  : false;

// =============================================================================
// Request Type Extraction
// =============================================================================

/**
 * Extract path parameters from a path definition
 */
export type PathParams<P extends ApiPaths, M extends PathMethods<P>> = paths[P][M] extends {
  parameters: { path: infer Params };
}
  ? Params
  : never;

/**
 * Extract query parameters from a path definition
 */
export type QueryParams<P extends ApiPaths, M extends PathMethods<P>> = paths[P][M] extends {
  parameters: { query?: infer Query };
}
  ? Query
  : never;

/**
 * Extract request body from a path definition
 */
export type RequestBody<P extends ApiPaths, M extends PathMethods<P>> = paths[P][M] extends {
  requestBody: { content: { 'application/json': infer Body } };
}
  ? Body
  : paths[P][M] extends { requestBody?: { content: { 'application/json': infer Body } } }
  ? Body | undefined
  : paths[P][M] extends { requestBody?: never }
  ? undefined
  : never;

// =============================================================================
// Response Type Extraction
// =============================================================================

/**
 * Extract successful response content from a path definition
 * Handles 200, 201, and 204 response codes
 */
export type SuccessResponse<P extends ApiPaths, M extends PathMethods<P>> = paths[P][M] extends {
  responses: infer R;
}
  ? R extends { 200: { content: { 'application/json': infer Data } } }
    ? Data
    : R extends { 201: { content: { 'application/json': infer Data } } }
    ? Data
    : R extends { 204: object }
    ? void
    : never
  : never;

/**
 * Extract the data field from a response (unwraps { data: T, meta: M })
 */
export type ResponseData<P extends ApiPaths, M extends PathMethods<P>> = SuccessResponse<P, M> extends {
  data?: infer D;
}
  ? D
  : SuccessResponse<P, M>;

/**
 * Extract the meta field from a response
 */
export type ResponseMeta<P extends ApiPaths, M extends PathMethods<P>> = SuccessResponse<P, M> extends {
  meta?: infer Meta;
}
  ? Meta
  : never;

// =============================================================================
// Pagination Types
// =============================================================================

/**
 * Standard pagination parameters
 */
export interface PaginationParams {
  limit?: number;
  page?: number;
}

/**
 * Pagination metadata from API responses
 */
export interface PaginationMeta {
  limit: number;
  page: number;
  total: number;
  totalPages: number;
}

/**
 * Paginated response structure
 */
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    message: string;
    pagination: PaginationMeta;
    requestId: string;
    status: number;
    timestamp: string;
  };
}

// =============================================================================
// API Response Wrapper Types
// =============================================================================

/**
 * Standard API response structure for single items
 */
export interface ApiResponse<T> {
  data: T;
  meta: {
    message: string;
    requestId: string;
    status: number;
    timestamp: string;
  };
}

/**
 * API error response structure
 */
export interface ApiError {
  code?: string;
  errors?: Array<{
    code?: string;
    field?: string;
    message?: string;
  }>;
  meta?: {
    errorCode?: string;
    errors?: Array<{
      code?: string;
      field?: string;
      message?: string;
    }>;
    message?: string;
    status?: number;
  };
  message: string;
  statusCode: number;
}

// =============================================================================
// Path Parameter Utilities
// =============================================================================

/**
 * Primitive types that can be used in query parameters
 */
type QueryParamValue = string | number | boolean | null | undefined;

/**
 * Query parameter object type - more specific than Record<string, unknown>
 */
export type QueryParamRecord = Record<string, QueryParamValue | QueryParamValue[]>;

/**
 * Extracts resource type from an API path
 */
export function getResourceType(path: string): ResourceType | null {
  // Check more specific paths first to avoid false matches
  if (path.includes('/out-of-office')) return ResourceType.OUT_OF_OFFICE;
  if (path.includes('/approvals')) return ResourceType.APPROVALS;
  if (path.includes('/users')) return ResourceType.USERS;
  if (path.includes('/labor-categories')) return ResourceType.LABOR_CATEGORIES;
  if (path.includes('/role-assignments')) return ResourceType.ROLE_ASSIGNMENTS;
  if (path.includes('/roster')) return ResourceType.ROSTER;
  if (path.includes('/contracts')) return ResourceType.CONTRACTS;
  if (path.includes('/applications')) return ResourceType.APPLICATIONS;

  return null;
}

/**
 * Replace path parameters in a URL template
 * e.g., '/api/v1/users/{id}' with { id: '123' } becomes '/api/v1/users/123'
 */
export function buildPath<P extends ApiPaths>(path: P, params?: Record<string, string | number>): string {
  if (!params) return path as string;

  let result = path as string;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`{${key}}`, String(value));
  }

  return result;
}

/**
 * Build URL with query parameters
 */
export function buildUrl(path: string, queryParams?: QueryParamRecord): string {
  if (!queryParams) return path;

  const filteredParams = Object.entries(queryParams).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );

  if (filteredParams.length === 0) return path;

  const searchParams = new URLSearchParams();
  for (const [key, value] of filteredParams) {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== null && v !== undefined) {
          searchParams.append(key, String(v));
        }
      });
    } else {
      searchParams.append(key, String(value));
    }
  }

  return `${path}?${searchParams.toString()}`;
}
